/**
 * src/lib/format.js — display helpers shared across pages.
 *
 * All timestamps in the API are unix seconds in UTC. Every formatter here
 * takes an optional `timeZone` (an IANA zone name, from the studio's settings
 * — see lib/auth.jsx's `timezone`); when a caller has not been updated to
 * pass one, it falls back to the browser's own zone exactly as before, so
 * this is a backward-compatible addition, not a breaking change.
 *
 * Staff should generally see the *studio's* zone, not their own — an artist
 * checking their phone while travelling should see the same appointment time
 * a colleague at the studio does. Client-facing pages pass the studio's zone
 * for the same reason: the studio's stated working hours mean its own zone.
 */
import { zonedParts, zonedTimeToUnix } from './timezone.js';

/**
 * Formats a timestamp as a date and time.
 *
 * @param {number|null|undefined} unixSeconds
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {string} e.g. "Thu 1 Oct, 14:00", or "—" when absent.
 */
export function formatDateTime(unixSeconds, timeZone) {
  if (!unixSeconds) return '—';
  return new Date(unixSeconds * 1000).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Formats a timestamp as a date only.
 *
 * @param {number|null|undefined} unixSeconds
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {string} e.g. "1 Oct 2026", or "—" when absent.
 */
export function formatDate(unixSeconds, timeZone) {
  if (!unixSeconds) return '—';
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Formats a timestamp as a time only.
 *
 * @param {number|null|undefined} unixSeconds
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {string} e.g. "14:00".
 */
export function formatTime(unixSeconds, timeZone) {
  if (!unixSeconds) return '—';
  return new Date(unixSeconds * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Formats minor units as currency.
 *
 * @param {number|null|undefined} minorUnits e.g. 5000.
 * @param {string} [currency] ISO code.
 * @returns {string} e.g. "£50.00".
 */
export function formatMoney(minorUnits, currency = 'GBP') {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((minorUnits ?? 0) / 100);
}

/**
 * Converts a datetime-local input value to unix seconds.
 *
 * `<input type="datetime-local">` yields a local-time string with no zone.
 * With `timeZone` given, that wall-clock time is interpreted as being in the
 * studio's zone (what an artist typing "14:00" into a booking means); without
 * it, `Date.parse` reads it as the browser's own zone, as before.
 *
 * @param {string} value e.g. "2026-10-01T14:00".
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {number} Unix seconds.
 */
export function localInputToUnix(value, timeZone) {
  if (!timeZone) return Math.floor(new Date(value).getTime() / 1000);
  const [datePart, timePart] = value.split('T');
  return zonedTimeToUnix(datePart, timePart, timeZone);
}

/**
 * Converts unix seconds to a datetime-local input value.
 *
 * @param {number} unixSeconds
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {string} e.g. "2026-10-01T14:00".
 */
export function unixToLocalInput(unixSeconds, timeZone) {
  const pad = (n) => String(n).padStart(2, '0');
  if (!timeZone) {
    const d = new Date(unixSeconds * 1000);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  const p = zonedParts(unixSeconds, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Start of the week (Monday) containing a date, as unix seconds.
 *
 * @param {Date} date
 * @param {string} [timeZone] IANA zone name; omit for the browser's own zone.
 * @returns {number}
 */
export function startOfWeek(date, timeZone) {
  if (!timeZone) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // getDay() is 0 for Sunday; shift so Monday is the first column.
    const offset = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - offset);
    return Math.floor(d.getTime() / 1000);
  }

  const p = zonedParts(Math.floor(date.getTime() / 1000), timeZone);
  // weekday is 0 for Sunday; shift so Monday is the first column. Subtracting
  // whole calendar days (via a UTC-anchored Date, purely for its date-rolling
  // arithmetic) rather than 86400-second multiples avoids landing an hour off
  // when the week under way crosses a DST transition.
  const offsetDays = (p.weekday + 6) % 7;
  const monday = new Date(Date.UTC(p.year, p.month - 1, p.day));
  monday.setUTCDate(monday.getUTCDate() - offsetDays);
  const dateStr = `${monday.getUTCFullYear()}-${String(monday.getUTCMonth() + 1).padStart(2, '0')}-${String(monday.getUTCDate()).padStart(2, '0')}`;
  return zonedTimeToUnix(dateStr, '00:00', timeZone);
}
